import type { SubjectLeftAction } from '@Application/Subjects/Actions/subjectLeft';
import BENEFICIARY_SUBJECT_TYPE_KEY from '@Domain/Subjects/BeneficiarySubjectTypeKey';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import type { BeneficiaryDirectoryFetchedAction } from '@Application/Beneficiaries/BeneficiaryDirectory/Actions/beneficiaryDirectoryFetched';
import type { BeneficiariesFetchedAction } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiariesFetched';
import type { BeneficiaryArchivedAction } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiaryArchived';
import type { BeneficiaryCreatedAction } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiaryCreated';
import type { BeneficiaryDeletedAction } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiaryDeleted';
import type { BeneficiaryFetchedAction } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiaryFetched';
import type { BeneficiaryHomonymsSearchedAction } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiaryHomonymsSearched';
import type { BeneficiaryHomonymsSearchFailedAction } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiaryHomonymsSearchFailed';
import type { BeneficiaryStaffMembersUpdatedAction } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiaryStaffMembersUpdated';
import type { BeneficiaryUpdatedAction } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiaryUpdated';
import BeneficiariesListView from '@UI/Store/Beneficiaries/Views/BeneficiariesListView';
import BeneficiaryDirectoryEntryView from '@UI/Store/Beneficiaries/Views/BeneficiaryDirectoryEntryView';
import BeneficiaryDirectoryListView from '@UI/Store/Beneficiaries/Views/BeneficiaryDirectoryListView';
import BeneficiaryListItemView from '@UI/Store/Beneficiaries/Views/BeneficiaryListItemView';
import BeneficiaryView from '@UI/Store/Beneficiaries/Views/BeneficiaryView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

// `directory` is kept apart from `beneficiaries`: the latter is the Beneficiaries screen's own
// filtered, cached page, and folding the (unfiltered, 200-row) directory into it would overwrite that
// page under a matching `accountId`.
export type BeneficiariesStore = {
	beneficiaries: BeneficiariesListView | null;
	beneficiary: BeneficiaryView | null;
	directory: BeneficiaryDirectoryListView | null;
};

const initialState: BeneficiariesStore = {
	beneficiaries: null,
	beneficiary: null,
	directory: null,
};

type Actions =
	| BeneficiariesFetchedAction
	| BeneficiaryFetchedAction
	| BeneficiaryHomonymsSearchedAction
	| BeneficiaryHomonymsSearchFailedAction
	| BeneficiaryCreatedAction
	| BeneficiaryUpdatedAction
	| BeneficiaryStaffMembersUpdatedAction
	| BeneficiaryArchivedAction
	| BeneficiaryDeletedAction
	| BeneficiaryDirectoryFetchedAction
	| SubjectLeftAction
	| UserRevokedAction;

const whenBeneficiariesFetched = (
	store: BeneficiariesStore,
	action: BeneficiariesFetchedAction,
): BeneficiariesStore => ({
	...store,
	beneficiaries: new BeneficiariesListView(
		action.payload.results.map((beneficiary) => new BeneficiaryListItemView(beneficiary)),
		action.accountId,
		action.payload.totalRecords,
		action.queryKey,
	),
});

const whenBeneficiaryFetched = (
	store: BeneficiariesStore,
	action: BeneficiaryFetchedAction,
): BeneficiariesStore => ({
	...store,
	beneficiary: new BeneficiaryView(action.payload),
});

// The homonym-check response is consumed by the caller through the *returned* action
// (`useBeneficiaryNameHomonymCheck`), never through the store: a dedicated slot would grow the state for
// a value that never survives the next keystroke, and reusing the list slot would overwrite what V-11
// shows. Both handlers are declared explicitly, rather than left as an unhandled action type, so the
// choice to keep nothing is visible here rather than merely implied.
const whenBeneficiaryHomonymsSearched = (store: BeneficiariesStore): BeneficiariesStore => store;

const whenBeneficiaryHomonymsSearchFailed = (store: BeneficiariesStore): BeneficiariesStore => store;

// Every write below empties `directory`, which the screen of the gesture never shows.
// Only the server knows whether the new dossier belongs to the filtered list: invalidate it.
const whenBeneficiaryCreated = (store: BeneficiariesStore): BeneficiariesStore => ({
	...store,
	beneficiaries: null,
	directory: null,
});

const whenBeneficiaryUpdated = (
	store: BeneficiariesStore,
	action: BeneficiaryUpdatedAction,
): BeneficiariesStore => {
	const beneficiaries = store.beneficiaries
		? store.beneficiaries.updateBeneficiary(new BeneficiaryListItemView(action.payload))
		: store.beneficiaries;

	const beneficiary =
		store.beneficiary !== null && store.beneficiary.id === action.payload.id
			? new BeneficiaryView(action.payload)
			: store.beneficiary;

	return { ...store, beneficiaries, beneficiary, directory: null };
};

// Ownership checked before mutating (`guides/state-stores.md`): a stale response for a dossier the
// screen has since navigated away from must never overwrite the one currently displayed.
const whenBeneficiaryStaffMembersUpdated = (
	store: BeneficiariesStore,
	action: BeneficiaryStaffMembersUpdatedAction,
): BeneficiariesStore => {
	if (store.beneficiary === null || store.beneficiary.id !== action.beneficiaryId) {
		return store;
	}

	return {
		...store,
		beneficiary: store.beneficiary.withStaffMembers(
			action.payload.nursingStaffMemberId,
			action.payload.homeCareStaffMemberId,
		),
	};
};

const whenBeneficiaryArchived = (
	store: BeneficiariesStore,
	action: BeneficiaryArchivedAction,
): BeneficiariesStore => {
	const beneficiaries = store.beneficiaries
		? store.beneficiaries.updateBeneficiary(new BeneficiaryListItemView(action.payload))
		: store.beneficiaries;

	// `PUT …/status` answers without the INS: rebuild the detail from its own current INS rather than
	// `new BeneficiaryView(...)`, which would erase it from the screen.
	const beneficiary =
		store.beneficiary !== null && store.beneficiary.id === action.payload.id
			? store.beneficiary.withListFields(action.payload)
			: store.beneficiary;

	return { ...store, beneficiaries, beneficiary, directory: null };
};

const whenBeneficiaryDeleted = (
	store: BeneficiariesStore,
	action: BeneficiaryDeletedAction,
): BeneficiariesStore => {
	const beneficiaries = store.beneficiaries
		? store.beneficiaries.removeBeneficiary(action.payload.beneficiaryId)
		: store.beneficiaries;

	const beneficiary =
		store.beneficiary !== null && store.beneficiary.id === action.payload.beneficiaryId
			? null
			: store.beneficiary;

	return { ...store, beneficiaries, beneficiary, directory: null };
};

const whenBeneficiaryDirectoryFetched = (
	store: BeneficiariesStore,
	action: BeneficiaryDirectoryFetchedAction,
): BeneficiariesStore => ({
	...store,
	directory: new BeneficiaryDirectoryListView(
		action.facilityId,
		action.assignedUserId,
		action.payload.results.map((entry) => new BeneficiaryDirectoryEntryView(entry)),
	),
});

const whenSubjectLeft = (store: BeneficiariesStore, action: SubjectLeftAction): BeneficiariesStore => {
	const { typeKey, subjectId } = action.payload;

	if (typeKey !== BENEFICIARY_SUBJECT_TYPE_KEY || store.beneficiary?.id !== subjectId) {
		return store;
	}

	return { ...store, beneficiary: null };
};

const whenUserRevoked = (): BeneficiariesStore => initialState;

export default registerStore<'beneficiariesStore', Actions>(
	'beneficiariesStore',
	createSliceReducer({
		BENEFICIARIES_FETCHED: whenBeneficiariesFetched,
		BENEFICIARY_FETCHED: whenBeneficiaryFetched,
		BENEFICIARY_HOMONYMS_SEARCHED: whenBeneficiaryHomonymsSearched,
		BENEFICIARY_HOMONYMS_SEARCH_FAILED: whenBeneficiaryHomonymsSearchFailed,
		BENEFICIARY_CREATED: whenBeneficiaryCreated,
		BENEFICIARY_UPDATED: whenBeneficiaryUpdated,
		BENEFICIARY_STAFF_MEMBERS_UPDATED: whenBeneficiaryStaffMembersUpdated,
		BENEFICIARY_ARCHIVED: whenBeneficiaryArchived,
		BENEFICIARY_DELETED: whenBeneficiaryDeleted,
		BENEFICIARY_DIRECTORY_FETCHED: whenBeneficiaryDirectoryFetched,
		SUBJECT_LEFT: whenSubjectLeft,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
